/**
 * Plan 10 (spec 2026-10-02 admin-keuskupan-di-paroki, 4): the parish an admin keuskupan works
 * in, remembered on this device for that person only (`simu:bekerja-di`, one entry per account,
 * as the display copies of plan 9b): another person on a shared device never reads it, and the
 * wipe removes it. The worker reads it with every scope (db/client `kerjaUntuk`) and the session
 * store with every write of the person (./sesi), so both open in the same place. No React, and
 * no import at run time (`@satu/shared` and the two local modules give types only): the launch
 * path (auth/wipe) and the node tests load it.
 */
import type { Peran } from '@satu/shared'
import type { TempatKerja } from '../../db/lingkupKerja'
import type { StorageLike } from './tampilan'

export type { TempatKerja }

// Legacy storage name from SIMU; kept so devices keep their data (pinned: test/nama-warisan.test.ts).
export const TEMPAT_KERJA_KEY = 'simu:bekerja-di'

const objek = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

/** Every person's entry, whatever the key holds (a value that cannot be read is none). */
function semua(storage: StorageLike | null): Record<string, TempatKerja> {
  if (storage === null) return {}
  let raw: unknown
  try {
    raw = JSON.parse(storage.getItem(TEMPAT_KERJA_KEY) ?? 'null')
  } catch {
    return {}
  }
  if (!objek(raw) || raw.v !== 1 || !objek(raw.orang)) return {}
  const out: Record<string, TempatKerja> = {}
  for (const [userId, t] of Object.entries(raw.orang)) {
    if (userId === '' || userId === '__proto__' || !objek(t)) continue
    const { parokiId, nama, pendek } = t
    if (typeof parokiId === 'string' && parokiId !== '' && typeof nama === 'string') {
      out[userId] = { parokiId, nama, pendek: typeof pendek === 'string' ? pendek : nama }
    }
  }
  return out
}

/** The parish `userId` chose on this device; null: the diocese view, or nothing stored. */
export function bacaTempatKerja(storage: StorageLike | null, userId: string): TempatKerja | null {
  if (userId === '') return null
  const orang = semua(storage)
  return Object.hasOwn(orang, userId) ? (orang[userId] ?? null) : null
}

/**
 * Remembers `userId`'s choice (null: the diocese view); every other person's stays as it is.
 * Answers whether this device kept it (false: no storage, or full or blocked).
 */
export function simpanTempatKerja(
  storage: StorageLike | null,
  userId: string,
  tempat: TempatKerja | null,
): boolean {
  if (storage === null || userId === '') return false
  const orang = Object.fromEntries(
    Object.entries(semua(storage)).filter(([lain]) => lain !== userId),
  )
  if (tempat !== null) {
    orang[userId] = { parokiId: tempat.parokiId, nama: tempat.nama, pendek: tempat.pendek }
  }
  try {
    storage.setItem(TEMPAT_KERJA_KEY, JSON.stringify({ v: 1, orang }))
    return true
  } catch {
    // Full or blocked: the switch keeps the diocese view, in the worker as on screen (./sesi).
    return false
  }
}

/** What the worker gets with a scope: the person's stored parish, for an admin keuskupan only. */
export function kerjaTersimpan(
  storage: StorageLike | null,
  scope: { user_id: string; peran: Peran },
): string | null {
  if (scope.peran !== 'admin_keuskupan') return null
  return bacaTempatKerja(storage, scope.user_id)?.parokiId ?? null
}
